import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import report from '../../../../utils/api/reports';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  totalPowerConsumed: number;
  date?: string;
  hour?: number | string;
  time?: string;
  documentCount?: number;
  [key: string]: string | number | undefined;
}

/**
 * Safely extracts YYYY-MM-DD string from a date value without timezone shift.
 * @param {any} rawDate - The raw date value (string or date).
 * @returns {string} The ISO date string YYYY-MM-DD.
 */
function extractIsoDateString(rawDate: any): string {
  if (!rawDate) return '';
  const dateString = String(rawDate).trim();
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    return dateString.substring(0, 10);
  }
  return dayjs(rawDate).format('YYYY-MM-DD');
}

/**
 * Safely parses power consumed value as number without floating precision artifacts.
 * @param {any} value - The raw power consumed value.
 * @returns {number} The parsed numeric value.
 */
function parsePowerConsumed(value: any): number {
  if (value === null || value === undefined || value === '') return 0;
  const parsedNumber = Number(value);
  if (Number.isNaN(parsedNumber)) return 0;
  return Number(parsedNumber.toFixed(4));
}

/**
 * Normalizes and fills missing data for hourly or daily energy report trends.
 * @param {any[]} rawData - The raw data array returned from the API.
 * @param {string} [startDateParameter] - Optional start date string (YYYY-MM-DD).
 * @param {string} [endDateParameter] - Optional end date string (YYYY-MM-DD).
 * @returns {ChartDataProperties[]} Complete list of data points with missing periods filled with 0.
 */
function buildTrendChartData(
  rawData: any[],
  startDateParameter?: string,
  endDateParameter?: string,
): ChartDataProperties[] {
  const rawList = Array.isArray(rawData) ? rawData : [];

  const hasHour = rawList.some(
    (item: any) =>
      (item.hour !== undefined && item.hour !== null && item.hour !== '') ||
      (item.time !== undefined && item.time !== null && item.time !== ''),
  );

  const isHourly =
    hasHour ||
    Boolean(
      startDateParameter &&
        endDateParameter &&
        startDateParameter === endDateParameter,
    );

  // 1. Hourly handling (e.g., 00:00 to 23:00)
  if (isHourly) {
    const rawDateFound = rawList.find((item: any) => item?.date)?.date;
    const fallbackDate =
      startDateParameter ||
      (rawDateFound ? extractIsoDateString(rawDateFound) : dayjs().format('YYYY-MM-DD'));

    const result: ChartDataProperties[] = [];
    for (let hourIndex = 0; hourIndex < 24; hourIndex += 1) {
      const hourPad = String(hourIndex).padStart(2, '0');
      const timeLabel = `${hourPad}:00`;

      const matchedItems = rawList.filter((item: any) => {
        if (item.hour !== undefined && item.hour !== null && item.hour !== '') {
          const itemHour = Number(item.hour);
          if (itemHour === hourIndex) return true;
          if (hourIndex === 0 && itemHour === 24) return true;
        }
        if (item.time !== undefined && item.time !== null && item.time !== '') {
          const timeString = String(item.time).trim();
          const hourPart = Number(timeString.split(':')[0]);
          if (!Number.isNaN(hourPart) && hourPart === hourIndex) return true;
        }
        return false;
      });

      const totalPower = matchedItems.reduce(
        (sum, item) => sum + (Number(item.totalPowerConsumed) || 0),
        0,
      );
      const totalDocuments = matchedItems.reduce(
        (sum, item) => sum + (Number(item.documentCount) || 0),
        0,
      );

      result.push({
        label: timeLabel,
        totalPowerConsumed: parsePowerConsumed(totalPower),
        hour: hourIndex,
        time: timeLabel,
        date: fallbackDate,
        documentCount: totalDocuments,
      });
    }
    return result;
  }

  // 2. Daily / Date-based handling
  const startDayjs = startDateParameter ? dayjs(startDateParameter) : null;
  const endDayjs = endDateParameter ? dayjs(endDateParameter) : null;

  const allIsoDates = rawList
    .map((item: any) => extractIsoDateString(item.date))
    .filter(Boolean)
    .sort();

  let effectiveStart: dayjs.Dayjs | null = null;
  if (startDayjs && startDayjs.isValid()) {
    effectiveStart = startDayjs;
  } else if (allIsoDates.length > 0) {
    effectiveStart = dayjs(allIsoDates[0]);
  }

  let effectiveEnd: dayjs.Dayjs | null = null;
  if (endDayjs && endDayjs.isValid()) {
    effectiveEnd = endDayjs;
  } else if (allIsoDates.length > 0) {
    effectiveEnd = dayjs(allIsoDates[allIsoDates.length - 1]);
  }

  if (
    effectiveStart &&
    effectiveEnd &&
    effectiveStart.isValid() &&
    effectiveEnd.isValid() &&
    (effectiveStart.isBefore(effectiveEnd) || effectiveStart.isSame(effectiveEnd, 'day'))
  ) {
    const dataMap = new Map<string, { totalPowerConsumed: number; documentCount: number }>();
    rawList.forEach((item: any) => {
      if (item.date) {
        const isoDate = extractIsoDateString(item.date);
        const previousRecord = dataMap.get(isoDate) || { totalPowerConsumed: 0, documentCount: 0 };
        dataMap.set(isoDate, {
          totalPowerConsumed: previousRecord.totalPowerConsumed + (Number(item.totalPowerConsumed) || 0),
          documentCount: previousRecord.documentCount + (Number(item.documentCount) || 0),
        });
      }
    });

    const result: ChartDataProperties[] = [];
    let currentDay = effectiveStart;
    const stopDay = effectiveEnd;

    while (currentDay.isBefore(stopDay) || currentDay.isSame(stopDay, 'day')) {
      const dayKey = currentDay.format('YYYY-MM-DD');
      const existing = dataMap.get(dayKey);

      result.push({
        label: currentDay.format('DD MMM'),
        totalPowerConsumed: parsePowerConsumed(existing ? existing.totalPowerConsumed : 0),
        date: dayKey,
        documentCount: existing?.documentCount || 0,
      });
      currentDay = currentDay.add(1, 'day');
    }

    return result;
  }

  // Fallback: direct mapping of rawList
  return rawList.map((item: any) => {
    const isoDate = extractIsoDateString(item.date);
    return {
      label: isoDate ? dayjs(isoDate).format('DD MMM') : (item.label || item.month || ''),
      totalPowerConsumed: parsePowerConsumed(item.totalPowerConsumed),
      date: isoDate,
      documentCount: item.documentCount || 0,
    };
  });
}

/**
 * Energy report trend chart
 * This component displays the energy consumption trend over time.
 * @returns {React.ReactElement} The rendered component
 */
function EnergyReportTrendChart(): React.ReactElement {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const reportsData = useAppSelector((state) => state.reportData);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate title for chart tooltip
           * @param {any[]} tooltipItems - Tooltip items
           * @returns {string} The tooltip title
           */
          title: (tooltipItems: any[]): string => {
            const index = tooltipItems?.[0]?.dataIndex;
            const item = barGraphData[index];
            if (!item) return `${t('powerConsumed')} (kW)`;
            if (item.date && item.time) {
              const formattedDate = dayjs(item.date).isValid()
                ? dayjs(item.date).format('DD MMM YYYY')
                : item.date;
              return `${formattedDate} ${item.time}`;
            }
            if (item.date && item.hour !== undefined && item.hour !== null) {
              const formattedDate = dayjs(item.date).isValid()
                ? dayjs(item.date).format('DD MMM YYYY')
                : item.date;
              return `${formattedDate} ${String(item.hour).padStart(2, '0')}:00`;
            }
            if (item.date) {
              return dayjs(item.date).isValid()
                ? dayjs(item.date).format('DD MMM YYYY')
                : item.date;
            }
            return item.label || `${t('powerConsumed')} (kW)`;
          },
          /**
           * Formats the tooltip label using locale string.
           * @param {any} context - The tooltip context object.
           * @returns {any} The formatted label string.
           */
          label: (context: any): string => `${t('powerConsumed')}: ${ToFixedValue(Number(context.raw), 3)} kW`,
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
    },
  };

  const verticalHoverLinePlugin = {
    id: 'verticalHoverLine',
    /**
     * Draws a black vertical crosshair line at the hovered x position.
     * @param {any} chart - The Chart.js instance
     */
    afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        const successDark = getComputedStyle(document.documentElement)
          .getPropertyValue('--success')
          .trim();

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = successDark;
        context.stroke();
        context.restore();
      }
    },
  };

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();

  /**
   * Fetches the power consumed report data for the specified date range and updates the chart data.
   * @param {string} [startDateParameter] - Optional start date (YYYY-MM-DD).
   * @param {string} [endDateParameter] - Optional end date (YYYY-MM-DD).
   */
  const fetchPowerConsumedReport = useCallback(
    async (startDateParameter?: string, endDateParameter?: string) => {
      setIsLoading(true);
      try {
        const activeStartDate =
          startDateParameter || reportsData?.reportFilter?.startDate;
        const activeEndDate =
          endDateParameter || reportsData?.reportFilter?.endDate;

        const queryParameters: any = {
          reportType: 'powerConsumed',
          accountCustomId: reportsData?.accountId,
        };

        if (activeStartDate) queryParameters.startDate = activeStartDate;
        if (activeEndDate) queryParameters.endDate = activeEndDate;

        const response = await report.fetchReportchart(queryParameters);

        let chartData: ChartDataProperties[] = [];

        if (response.status === 200) {
          chartData = buildTrendChartData(response.data, activeStartDate, activeEndDate);
        }

        setBarGraphData(chartData);
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      reportsData?.reportFilter?.startDate,
      reportsData?.reportFilter?.endDate,
      reportsData?.accountId,
      dispatch,
    ],
  );

  /**
   * If clicking the already active pin, deactivate it
   * @param {any} name - The name
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    const activeStart = reportsData.reportFilter.startDate;
    const activeEnd = reportsData.reportFilter.endDate;
    if (activeStart && activeEnd) {
      fetchPowerConsumedReport(activeStart, activeEnd);
    } else {
      fetchPowerConsumedReport();
    }
  }, [
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.accountId,
    fetchPowerConsumedReport,
  ]);

  const labels = barGraphData.map((d) => d.label || d.monthName || '');
  const values = barGraphData.map((d) => d.totalPowerConsumed || 0);

  return (
    <div className="energy-report-chart trend-chart-full-width">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('powerConsumed') ? 'active' : ''}`}
              onClick={() => handleClick('powerConsumed')}>
              {reportsData.activePinName.includes('powerConsumed') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('energyUsageTrend')}
          </div>
        </div>

        <div className="energy-report-container chart-canvas-container">
          {isLoading ? (
            <div className="chart-loader-container">
              <BatteryLoader />
            </div>
          ) : (
            <Line
              plugins={[verticalHoverLinePlugin]}
              data={{
                labels,
                datasets: [
                  {
                    label: t('powerConsumed'),
                    data: values,
                    borderColor: primaryColor,
                    backgroundColor: primaryColor,
                    fill: false,
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointBackgroundColor: primaryColor,
                    pointBorderColor: primaryColor,
                  },
                ],
              }}
              options={chartOptions}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default EnergyReportTrendChart;
